import type { AllocationResult } from '@whaletail/signal-engine';
import type { Btc15Quote } from '../../lib/kalshiBtc15';
import { formatSignedPercent, formatUsd } from '../../lib/format';
import styles from './Btc15MarketSizing.module.css';

/**
 * The sizing row in the round hero: the live ask on the model's side, the
 * model's edge over it, and the simulated buy that edge supports. Shown
 * until the round has a simulated position (which then carries its own
 * entry and stake).
 */
export function Btc15MarketSizing({
  quote,
  allocation,
  showReason = true,
}: {
  quote: Btc15Quote;
  allocation: AllocationResult;
  /** Off when the reason is already shown elsewhere (e.g. under the call). */
  showReason?: boolean;
}) {
  if (quote.source === 'unavailable' || quote.contractPrice === null) {
    return (
      <section className={styles.inline} aria-labelledby="btc15-market-heading">
        <h2 id="btc15-market-heading" className="visually-hidden">
          Market &amp; sizing
        </h2>
        <p className={styles.unavailable}>
          No live Kalshi quote for this round yet.{' '}
          {quote.error ? `(${quote.error})` : null}
        </p>
      </section>
    );
  }

  // KXBTC15M: buying UP is the YES contract, DOWN is NO.
  const side = quote.action === 'UP' ? 'YES' : 'NO';

  return (
    <section className={styles.inline} aria-labelledby="btc15-market-heading">
      <h2 id="btc15-market-heading" className="visually-hidden">
        Market &amp; sizing
      </h2>
      <div className={styles.inlineGrid}>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>{side} ask</span>
          <span className={styles.metricValue}>
            {(quote.contractPrice * 100).toFixed(1)}¢
          </span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Model edge</span>
          <span className={styles.metricValue}>
            {allocation.edge === null
              ? '—'
              : formatSignedPercent(allocation.edge * 100, 1)}
          </span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Simulated buy</span>
          <span className={`${styles.metricValue} ${styles.buyHighlight}`}>
            {formatUsd(allocation.stake)}
          </span>
        </div>
      </div>
      {showReason && <p className={styles.reason}>{allocation.reason}</p>}
    </section>
  );
}
